import React, { useEffect, useState, useRef, useCallback } from 'react';
import ScormService from '../services/scormService';
import { completeModule, updateModuleProgress } from '../services/api';
import { toast } from 'react-toastify';
import {
  ScormProgressTracker,
  isCompletionUrl,
} from '../utils/scormProgress';

interface ScormWrapperProps {
  courseId: string;
  moduleId: string;
  userId: string;
  courseUrl: string;
  initialProgress?: number;
  onError?: (error: Error) => void;
  onProgress?: (progress: { progress: number; trigger: string }) => void;
  onCompletion?: () => void;
}

const persistProgress = (moduleId: string, courseId: string, progress: number) => {
  return updateModuleProgress(parseInt(moduleId, 10), progress, parseInt(courseId, 10));
};

const attachScorm2004Aliases = (api: Record<string, unknown>) => {
  api.Initialize = api.LMSInitialize;
  api.Terminate = api.LMSFinish;
  api.GetValue = api.LMSGetValue;
  api.SetValue = api.LMSSetValue;
  api.Commit = api.LMSCommit;
  api.GetLastError = api.LMSGetLastError;
  api.GetErrorString = api.LMSGetErrorString;
  api.GetDiagnostic = api.LMSGetDiagnostic;
};

const ScormWrapper: React.FC<ScormWrapperProps> = ({
  courseId,
  moduleId,
  userId,
  courseUrl,
  initialProgress = 0,
  onError,
  onProgress,
  onCompletion,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const scormServiceRef = useRef<ScormService | null>(null);
  const trackerRef = useRef(new ScormProgressTracker());
  const completionTriggeredRef = useRef(false);
  const startedRef = useRef(false);
  const cleanupRef = useRef<(() => void) | null>(null);
  const onProgressRef = useRef(onProgress);
  const onCompletionRef = useRef(onCompletion);
  const onErrorRef = useRef(onError);
  const initialProgressRef = useRef(initialProgress);

  onProgressRef.current = onProgress;
  onCompletionRef.current = onCompletion;
  onErrorRef.current = onError;
  initialProgressRef.current = initialProgress;

  const publishProgress = useCallback((progress: number, trigger: string) => {
    persistProgress(moduleId, courseId, progress).catch(() => {});
    if (onProgressRef.current) onProgressRef.current({ progress, trigger });
  }, [moduleId, courseId]);

  const handleCompletion = useCallback(async (trigger: string) => {
    if (completionTriggeredRef.current) return;
    completionTriggeredRef.current = true;
    trackerRef.current.applyFinish('completed');

    try {
      await persistProgress(moduleId, courseId, 100);
      await completeModule(parseInt(moduleId, 10), parseInt(courseId, 10));

      if (onProgressRef.current) onProgressRef.current({ progress: 100, trigger });
      if (onCompletionRef.current) {
        onCompletionRef.current();
      }
      toast.success('Course completed successfully!');
    } catch (err) {
      completionTriggeredRef.current = false;
      console.error('[SCORM] Error recording completion:', err);
      toast.error('Error recording course completion');
    }
  }, [moduleId, courseId]);

  const markStartedOnce = useCallback(() => {
    if (startedRef.current) {
      return trackerRef.current.progress;
    }
    startedRef.current = true;
    const progress = trackerRef.current.markStarted();
    publishProgress(progress, 'started');
    return progress;
  }, [publishProgress]);

  useEffect(() => {
    let mounted = true;

    const initialize = async () => {
      try {
        const service = new ScormService(courseId, userId, moduleId);
        const success = await service.initialize();

        if (!success || !mounted) {
          if (mounted) throw new Error('Failed to initialize SCORM service');
          return;
        }

        scormServiceRef.current = service;
        const restored = new ScormProgressTracker(initialProgressRef.current);
        const savedLocation = service.getLocation();
        if (savedLocation) {
          restored.applyValue('cmi.core.lesson_location', savedLocation);
        }
        const savedScore = service.getScore();
        if (savedScore > 0) {
          restored.applyValue('cmi.core.score.raw', String(savedScore));
        }
        restored.applyFinish(service.getLessonStatus());
        trackerRef.current = restored;
        startedRef.current = restored.progress > 0;
        completionTriggeredRef.current = false;
        if (restored.progress >= 100 && initialProgressRef.current < 100) {
          handleCompletion('restored');
        }

        const api: Record<string, any> = {
          LMSInitialize: (_param?: string) => {
            if (mounted) {
              setLoading(false);
              markStartedOnce();
            }
            return 'true';
          },

          LMSFinish: (_param?: string) => {
            if (scormServiceRef.current) {
              const previous = trackerRef.current.progress;
              const result = trackerRef.current.applyFinish(scormServiceRef.current.getLessonStatus());
              if (result.completed) {
                handleCompletion('LMSFinish');
              } else if (result.progress > previous) {
                publishProgress(result.progress, 'LMSFinish');
              }
              scormServiceRef.current.terminate();
            }
            return 'true';
          },

          LMSGetValue: (element: string) => {
            if (!scormServiceRef.current) return '';
            return scormServiceRef.current.getValue(element);
          },

          LMSSetValue: (element: string, value: string) => {
            if (!scormServiceRef.current) return 'false';
            const previous = trackerRef.current.progress;
            const result = scormServiceRef.current.setValue(element, String(value));
            const update = trackerRef.current.applyValue(element, String(value));

            if (update.completed) {
              handleCompletion('LMSSetValue');
            } else if (update.progress > previous) {
              publishProgress(update.progress, element);
            }

            return result ? 'true' : 'false';
          },

          LMSCommit: (_param?: string) => {
            if (scormServiceRef.current) {
              scormServiceRef.current.saveProgress();
              const previous = trackerRef.current.progress;
              const result = trackerRef.current.applyFinish(scormServiceRef.current.getLessonStatus());
              if (result.completed) {
                handleCompletion('LMSCommit');
              } else if (result.progress > previous) {
                publishProgress(result.progress, 'LMSCommit');
              }
            }
            return 'true';
          },

          LMSGetLastError: () => '0',
          LMSGetErrorString: (_code?: string) => '',
          LMSGetDiagnostic: (_code?: string) => '',
        };

        attachScorm2004Aliases(api);
        (window as any).API = api;
        (window as any).API_1484_11 = api;

        let allowedIframeOrigin = '';
        try {
          allowedIframeOrigin = new URL(courseUrl).origin;
        } catch {
          allowedIframeOrigin = '';
        }

        const handleBridgeMessage = (event: MessageEvent) => {
          const { data } = event;
          if (allowedIframeOrigin && event.origin !== allowedIframeOrigin) return;
          if (!data || typeof data !== 'object' || data.source !== 'scorm-bridge') return;

          if (data.type === 'scorm_bridge_loaded') {
            if (mounted) setLoading(false);
            const href = data.data?.href || data.data?.pathname || '';
            if (isCompletionUrl(href)) {
              handleCompletion('completion_url');
            }
            return;
          }

          if (data.type === 'scorm_initialized') {
            api.LMSInitialize();
          }

          if (data.type === 'scorm_set_value' && data.data) {
            const { parameter, value } = data.data;
            if (parameter && value !== undefined) {
              api.LMSSetValue(parameter, String(value));
            }
          }

          if (data.type === 'scorm_finished') {
            api.LMSFinish();
          }

          if (data.type === 'scorm_commit') {
            api.LMSCommit();
          }
        };

        window.addEventListener('message', handleBridgeMessage);

        cleanupRef.current = () => {
          window.removeEventListener('message', handleBridgeMessage);
          delete (window as any).API;
          delete (window as any).API_1484_11;
          if (scormServiceRef.current) {
            scormServiceRef.current.saveProgress().catch(() => {});
          }
        };

        markStartedOnce();

        if (mounted) {
          setError(null);
          setTimeout(() => {
            if (mounted) setLoading(false);
          }, 8000);
        }
      } catch (err) {
        if (mounted) {
          const msg = err instanceof Error ? err.message : 'Failed to initialize SCORM';
          setError(msg);
          setLoading(false);
          if (onErrorRef.current) onErrorRef.current(err instanceof Error ? err : new Error(msg));
        }
      }
    };

    initialize();

    return () => {
      mounted = false;
      if (cleanupRef.current) cleanupRef.current();
    };
  }, [courseId, moduleId, userId, courseUrl, handleCompletion, markStartedOnce, publishProgress]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50">
        <div className="text-center p-8">
          <h3 className="text-lg font-semibold text-red-600 mb-2">SCORM Error</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4" />
            <p className="text-gray-600">Loading course content...</p>
          </div>
        </div>
      )}
      <iframe
        ref={iframeRef}
        src={courseUrl}
        title="SCORM Content"
        className="w-full h-full border-0"
        allow="autoplay"
        onLoad={() => setLoading(false)}
      />
    </div>
  );
};

export default ScormWrapper;
